/** Project chat and camera photos (phase 5, the Projects board): the day
 *  lines, the words beside a photo, and what an attachment is refused for
 *  before it leaves the browser (the server checks every file again). */
import type { ProjectImage, ProjectMessage } from '../api/types'
import { formatDateTime } from './time'

export const MAX_MB = 200
const RAW = /\.(dng|cr2|cr3|crw|nef|nrw|arw|srf|sr2|raf|orf|rw2|pef|srw|x3f|3fr|iiq|rwl|raw)$/i

/** Each message with the day line the board draws above the first of a
 *  station-local day: Today, Yesterday, or the date. */
export function withDays(messages: ProjectMessage[], now = new Date()):
  { message: ProjectMessage; day: string | null }[] {
  const today = formatDateTime(now).slice(0, 10)
  const yesterday = formatDateTime(new Date(now.getTime() - 86_400_000)).slice(0, 10)
  let last = ''
  return messages.map((message) => {
    const day = formatDateTime(message.created_at).slice(0, 10)
    const label = day === last ? null : day === today ? 'Today' : day === yesterday ? 'Yesterday' : day
    last = day
    return { message, day: label }
  })
}

export function timeOf(at: string): string {
  return formatDateTime(at).slice(11, 16)
}

/** "Chkhuteli · 2026-10-20 09:30" under a thumbnail. */
export function imageMeta(image: ProjectImage): string {
  return `${image.station.name} · ${formatDateTime(image.created_at)}`
}

export function sizeLabel(bytes: number | null): string {
  if (!bytes) return ''
  return bytes >= 1e6 ? `${(bytes / 1e6).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1e3))} kB`
}

/** Why a file will not be kept, before it is sent, or null. */
export function fileRefusal(file: { name: string; size: number }, maxMb = MAX_MB): string | null {
  if (RAW.test(file.name)) {
    return `${file.name} is a camera RAW file. Upload a JPEG, PNG, WebP, TIFF or HEIC export of it.`
  }
  if (file.size > maxMb * 1_000_000) return `${file.name} is larger than ${maxMb} MB.`
  if (file.size === 0) return `${file.name} is empty.`
  return null
}

export function canSend(body: string, attached: boolean): boolean {
  return body.trim() !== '' || attached
}
